import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  MessageSquare, Plus, Send, Clock, CheckCircle, AlertCircle, 
  XCircle, ChevronRight, User, Headphones, ArrowLeft
} from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const CATEGORIES = [
  { value: 'payment', label: 'Payment Issue', icon: '💳' },
  { value: 'ticket', label: 'Ticket Problem', icon: '🎫' },
  { value: 'event', label: 'Event Related', icon: '📅' },
  { value: 'account', label: 'Account Issue', icon: '👤' },
  { value: 'technical', label: 'Technical Problem', icon: '🔧' },
  { value: 'other', label: 'Other', icon: '📝' },
];

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-yellow-500', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', icon: AlertCircle },
  resolved: { label: 'Resolved', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500', icon: XCircle },
};

export default function SupportTickets() {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, complaint: null });
  const [submitting, setSubmitting] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    message: ''
  });

  const fetchComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/complaints/my-complaints`);
      setComplaints(response.data);
    } catch (error) {
      console.error('Error fetching complaints:', error);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints]);

  const resetForm = () => {
    setFormData({ category: '', subject: '', message: '' });
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.subject || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/complaints`, formData);
      toast.success('Your complaint has been submitted!');
      setCreateDialog(false);
      resetForm();
      fetchComplaints();
    } catch (error) {
      console.error('Error submitting complaint:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/complaints/${detailDialog.complaint.complaint_id}/respond`,
        { message: replyMessage },
        
      );
      toast.success('Reply sent!');
      setReplyMessage('');
      
      // Refresh complaint detail
      const response = await axios.get(
        `${API_URL}/api/complaints/${detailDialog.complaint.complaint_id}`,
        
      );
      setDetailDialog({ open: true, complaint: response.data });
      fetchComplaints();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (complaint) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/complaints/${complaint.complaint_id}`,
        
      );
      setDetailDialog({ open: true, complaint: response.data });
    } catch (error) {
      toast.error('Failed to load complaint details');
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || cat;
  const getCategoryIcon = (cat) => CATEGORIES.find(c => c.value === cat)?.icon || '📝';

  const openComplaints = complaints.filter(c => c.status === 'open' || c.status === 'in_progress');
  const closedComplaints = complaints.filter(c => c.status === 'resolved' || c.status === 'closed');

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Support Center</h1>
            <p className="text-muted-foreground">Get help with your issues and track your tickets</p>
          </div>
          <Button onClick={() => setCreateDialog(true)} data-testid="new-ticket-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {/* Tickets List */}
        <Tabs defaultValue="active" className="space-y-6">
          <TabsList>
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Active ({openComplaints.length})
            </TabsTrigger>
            <TabsTrigger value="closed" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Resolved ({closedComplaints.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 skeleton rounded-xl" />
                ))}
              </div>
            ) : openComplaints.length > 0 ? (
              <div className="space-y-4">
                {openComplaints.map((complaint) => {
                  const statusConfig = getStatusConfig(complaint.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <Card 
                      key={complaint.complaint_id}
                      className="cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => openDetail(complaint)}
                      data-testid={`ticket-${complaint.complaint_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="text-2xl">{getCategoryIcon(complaint.category)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold truncate">{complaint.subject}</h3>
                              <Badge className={`${statusConfig.color} text-white text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">{complaint.message}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span>{getCategoryLabel(complaint.category)}</span>
                              <span>•</span>
                              <span>{format(new Date(complaint.created_at), 'MMM d, yyyy')}</span>
                              {complaint.responses?.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{complaint.responses.length} response{complaint.responses.length !== 1 ? 's' : ''}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No active tickets</h3>
                  <p className="text-muted-foreground mb-4">
                    You don't have any open support tickets.
                  </p>
                  <Button onClick={() => setCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Ticket
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="closed">
            {closedComplaints.length > 0 ? (
              <div className="space-y-4">
                {closedComplaints.map((complaint) => {
                  const statusConfig = getStatusConfig(complaint.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <Card 
                      key={complaint.complaint_id}
                      className="cursor-pointer hover:border-primary/50 transition-colors opacity-75"
                      onClick={() => openDetail(complaint)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="text-2xl">{getCategoryIcon(complaint.category)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold truncate">{complaint.subject}</h3>
                              <Badge className={`${statusConfig.color} text-white text-xs`}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1">{complaint.message}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No resolved tickets</h3>
                  <p className="text-muted-foreground">
                    Your resolved tickets will appear here.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Ticket Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Submit a Support Ticket
              </DialogTitle>
              <DialogDescription>
                Describe your issue and our support team will help you.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="ticket-category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <span>{cat.icon}</span>
                          <span>{cat.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  placeholder="Brief description of your issue"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  data-testid="ticket-subject-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder="Please describe your issue in detail..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={5}
                  data-testid="ticket-message-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-ticket-btn">
                {submitting ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ticket Detail Dialog */}
        <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, complaint: null })}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {detailDialog.complaint && (
              <>
                <DialogHeader>
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{getCategoryIcon(detailDialog.complaint.category)}</div>
                    <div className="flex-1">
                      <DialogTitle className="text-xl">{detailDialog.complaint.subject}</DialogTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${getStatusConfig(detailDialog.complaint.status).color} text-white`}>
                          {getStatusConfig(detailDialog.complaint.status).label}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {getCategoryLabel(detailDialog.complaint.category)}
                        </span>
                        <span className="text-sm text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(detailDialog.complaint.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                      </div>
                    </div>
                  </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto py-4 space-y-4">
                  {/* Original Message */}
                  <div className="p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4" />
                      <span className="font-medium">You</span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(detailDialog.complaint.created_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{detailDialog.complaint.message}</p>
                  </div>

                  {/* Responses */}
                  {detailDialog.complaint.responses?.map((response) => (
                    <div 
                      key={response.response_id}
                      className={`p-4 rounded-xl ${
                        response.is_closure 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : response.is_admin 
                            ? 'bg-primary/10 border border-primary/20' 
                            : 'bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {response.is_closure ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : response.is_admin ? (
                          <Headphones className="w-4 h-4 text-primary" />
                        ) : (
                          <User className="w-4 h-4" />
                        )}
                        <span className="font-medium">
                          {response.is_closure ? 'Case Resolution' : response.is_admin ? 'Support Team' : 'You'}
                        </span>
                        {response.is_admin && response.admin_role && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {response.admin_role.replace('_', ' ')}
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(response.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Section */}
                {detailDialog.complaint.status !== 'closed' && (
                  <div className="border-t pt-4">
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your reply..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={2}
                        className="flex-1"
                        data-testid="ticket-reply-input"
                      />
                      <Button 
                        onClick={handleReply} 
                        disabled={submitting || !replyMessage.trim()}
                        data-testid="send-reply-btn"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
